import * as Sentry from '@sentry/react';
import axios from 'axios';

import APIConstants from '@qality/cloud-services/src/APIConstants';
import AppPropertiesService from '@qality/cloud-services/src/AppPropertiesService';
import AuthenticateService from '@qality/cloud-services/src/AuthenticateService';
import ErrorHandlingUtils from '@qality/cloud-services/src/ErrorHandlingUtils';
import HostNameService from '@qality/cloud-services/src/HostNameService';
import { APResponse } from '@qality/common-model/src/AP/AP';
import { NewUserApplicationInfoResponse } from '@qality/common-service/src/UserService';

import {
  SENTRY_CONFIGURATION_PROPERTY,
  SentryAgreementProperty,
} from './Sentry.types';

export default {
  initSentry() {
    const hostNamePromise = HostNameService.init();
    const replay = Sentry.replayIntegration({
      blockAllMedia: true,
      maskAllText: false,
      maskAllInputs: true,
      mask: [
        '.atlaskit-portal',
        '.atlaskit-portal-container',
        '.solkit__menu-portal',
      ],
    });

    const shouldRecordBehaviorPromise = Promise.all([
      this.isSentryRecordingAllowed(),
      this.isNewUser(),
    ]);

    Sentry.init({
      dsn: plugin.sentryDsn,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        replay,
      ],

      environment: plugin.environment,
      release: plugin.version,
      tracesSampleRate: 0.1,
      tracePropagationTargets: ['atlassian.net', 'ngrok', 'soldevelo'],
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 0.0,
      ignoreErrors: ['ResizeObserver loop limit exceeded'],

      beforeBreadcrumb: (event) => {
        if (event.category === 'xhr') {
          if (typeof event.data?.url === 'string') {
            event.data.url = getURLWithRemovedJWT(event.data.url);
          }
        }

        return event;
      },

      beforeSend: (event) => {
        event.request.url = getURLWithRemovedJWT(event.request.url);
        return event;
      },

      beforeSendTransaction: (transaction) => {
        transaction.spans = transaction.spans.map((span) => {
          span.name = getURLWithRemovedJWT(span.name);

          return span;
        });

        return transaction;
      },
    });

    shouldRecordBehaviorPromise
      .then(([isSentryRecordingAllowed, isNewUser]) => {
        if (isSentryRecordingAllowed && isNewUser) {
          const currentReplay = Sentry.getReplay();
          currentReplay.start();
          return true;
        } else {
          return false;
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });

    Sentry.getCurrentScope().setTag('platform', plugin.runtimeEnvironment);
    Sentry.getCurrentScope().setTag('platformVersion', 'latest');

    hostNamePromise
      .then(() => {
        Sentry.getCurrentScope().setTag('hostname', HostNameService.baseUrl);
        return;
      })
      .catch(() => {
        Sentry.getCurrentScope().setTag('hostname', 'unknown');
      });
  },

  async isSentryRecordingAllowed(): Promise<boolean> {
    let userProperty: SentryAgreementProperty;

    try {
      const response: APResponse = (await AppPropertiesService.getAppProperty(
        SENTRY_CONFIGURATION_PROPERTY
      )) as APResponse;
      userProperty = JSON.parse(response.body).value as SentryAgreementProperty;
    } catch (e) {
      return false;
    }
    return userProperty.allowSentryRecording;
  },

  async isNewUser(): Promise<boolean> {
    try {
      const url: string = await AuthenticateService.authenticateByJWT(
        window.location.origin + APIConstants.URL_APPLICATION_INFO
      );

      const response: NewUserApplicationInfoResponse = (await axios.get(url))
        .data;
      return response.newUser;
    } catch (e) {
      ErrorHandlingUtils.handleError(
        e,
        'Failed to set user property ' + SENTRY_CONFIGURATION_PROPERTY
      );
      return false;
    }
  },

  async setSentryAgreementProperty(property: boolean) {
    try {
      const propertyValue: SentryAgreementProperty = {
        allowSentryRecording: property,
      };
      await AppPropertiesService.updateAppProperty(
        SENTRY_CONFIGURATION_PROPERTY,
        propertyValue
      );
    } catch (e) {
      ErrorHandlingUtils.handleError(
        e,
        'Failed to set user property ' + SENTRY_CONFIGURATION_PROPERTY
      );
    }
  },
};

const getURLWithRemovedJWT = (url = '') => {
  const [page, params] = url.split('?');
  const updatedParams = `&${params}`
    .replace(/&jwt=[^&]+/g, '&jwt=REMOVED_PARAM')
    .replace(/^&/, '?');
  return page + updatedParams;
};
